import moment from 'moment';

/**
 * Returns the soil moisture percentage value for a site.
 *
 * The calculation finds the percentage of summary.latest_reading_date_rz1
 * within the range of the refill point to the full point.
 *
 * @param {object} summary
 * @returns {(number|null)}
 */
export const CalculateSiteSoilMoisture = (summary) => {
  return summary
    ? (summary.latest_reading_date_rz1 - summary.refill_point) /
        (summary.full_point - summary.refill_point)
    : null;
};

/**
 * Returns a number between 1 and 3
 *  - 1 = Green (Site summary is looking good)
 *  - 2 = Orange (Site summary warning)
 *  - 3 = Red (Site needs action)
 *
 * @param {object} summary
 * @returns {number}
 */
export const CalculateSiteSummaryLevel = (summary) => {
  let outsiteLimits = false;
  let outsiteStrategy = false;
  let aboveLimits = false;

  if (
    summary.latest_reading_date_rz1 > summary.full_point ||
    summary.latest_reading_date_rz1 < summary.refill_point
  ) {
    if (summary.latest_reading_date_rz1 > summary.full_point) {
      aboveLimits = true;
    }
    outsiteLimits = true;
  }

  if (
    summary.latest_reading_date_rz1 > summary.strategy_max ||
    summary.latest_reading_date_rz1 < summary.strategy_min
  )
    outsiteStrategy = true;

  return outsiteStrategy && outsiteLimits
    ? aboveLimits
      ? 4
      : 3
    : !outsiteStrategy && outsiteLimits
    ? 2
    : outsiteStrategy && !outsiteLimits
    ? 2
    : 1;
};

export const SummaryBootstrapColorClass = (siteResults) => {
  const summaryLevel = siteResults.alert_level;
  const fullPoint = siteResults.full_point;
  const latestReading = siteResults.latest_reading.rz1;
  if (summaryLevel === 3 && latestReading > fullPoint) {
    return 'primary';
  }

  return summaryLevel === 3
    ? 'danger'
    : summaryLevel === 2
    ? 'warning'
    : 'success';
};

export const SummaryBootstrapColorClassLevel = (level) => {
  return level === 4
    ? 'primary'
    : level === 3
    ? 'danger'
    : level === 2
    ? 'warning'
    : 'success';
};

export const AlertLevelLabel = (alertLevel) =>
  alertLevel === 4
    ? 'Above Full Point'
    : alertLevel === 3
    ? 'Attention Required'
    : alertLevel === 2
    ? 'Outside Strategy'
    : 'Within Strategy';

export const formatSiteType = (type) => {
  if (type === 'irrimax') {
    return 'IrriMAX Continuous';
  } else if (type === 'terraprobe') {
    return 'Weekly';
  } else {
    return type;
  }
};

export const openIrrimaxWindow = (url) => {
  window.open(url, '_blank', 'width=800,height=600').focus();
};

export const openIrrimaxTab = (url) => {
  window.open(url, '_blank').focus();
};

export const openIrrimaxCurrentTab = (url) => {
  window.open(url, '_self').focus();
};

export const getSelectedSeason = async (startYear) => {
  return fetch(`${process.env.REACT_APP_TERRAPROBE_API_URL}api/season`)
    .then((res) => {
      if (res.ok) return res.json();
      else throw new Error(res.statusText);
    })
    .then((data) => {
      return data.find((season) => {
        const dt = moment(season.season_date, 'DD-MM-YYYY');
        return dt.year() === Number(startYear);
      });
    })
    .catch((err) => {
      if (process.env.NODE_ENV === 'development') console.error(err);
    });
};

const cacheWithTTL = async (cacheName, url, response, ttlSeconds) => {
  const cache = await caches.open(cacheName);

  const headers = new Headers(response.headers);
  headers.append('cache-timestamp', Date.now().toString());
  headers.append('cache-ttl', ttlSeconds.toString());

  const modifiedResponse = new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers: headers
  });

  await cache.put(url, modifiedResponse);
};

const getCachedResponse = async (cacheName, request) => {
  const cache = await caches.open(cacheName);
  const response = await cache.match(request);

  if (!response) {
    // Since the cached request contains a date that represents the current day,
    // all cached request should be deleted if no matching request. Otherwise,
    // some request could be cached indefinitely as making the request on a new day will
    // create a different request.
    const allCached = await cache.keys();
    for (const cached of allCached) {
      await cache.delete(cached);
    }
    return null;
  }

  const timestamp = response.headers.get('cache-timestamp');
  const ttl = response.headers.get('cache-ttl');

  if (timestamp && ttl) {
    const age = (Date.now() - parseInt(timestamp)) / 1000;
    if (age > parseInt(ttl)) {
      // Cache expired, delete it.
      await cache.delete(request);
      return null;
    }
  }
  return response;
};

const parseXmlToArray = (xmlText) => {
  const xmlDoc = new DOMParser().parseFromString(xmlText, 'text/xml');
  const eElements = xmlDoc.querySelectorAll('Data > E');
  const values = Array.from(eElements).map((e) => {
    const tElement = e.querySelector('T'); // Date.
    const i1Element = e.querySelector('I1'); // Water value.
    return {
      date: tElement.textContent.trim(),
      water: Number(i1Element.textContent.trim())
    };
  });
  return values;
};

export const ERROR_TYPES = {
  NoWaterConsentError: 1,
  NoWaterConsentDataError: 2,
  WaterConsentDataFetchError: 3,
  UnexpectedError: 4
};

export class NoWaterConsentError extends Error {
  constructor(message) {
    super(message);
    this.type = ERROR_TYPES.NoWaterConsentError;
  }
}

export class NoWaterConsentDataError extends Error {
  constructor(message) {
    super(message);
    this.type = ERROR_TYPES.NoWaterConsentDataError;
  }
}

export class WaterConsentDataFetchError extends Error {
  constructor(message) {
    super(message);
    this.type = ERROR_TYPES.WaterConsentDataFetchError;
  }
}

const waterConsentStartDate = (days) => {
  // Return 'days' if less than 1 year. Otherwise, calculate start date
  // considering the season start date of 1st of July.
  const now = moment();
  if (days < 365) return now.subtract({ days }).format('YYYY/MM/DD');
  const limitDate = moment({ year: now.year(), month: 6, day: 1 });
  if (now.isAfter(limitDate)) {
    const subtractDays = now.diff(limitDate, 'days');
    return now.subtract({ days: subtractDays }).format('YYYY/MM/DD');
  } else if (now.isBefore(limitDate)) {
    const limitDate = moment({ year: now.year() - 1, month: 6, day: 1 });
    const subtractDays = now.diff(limitDate, 'days');
    return now.subtract({ days: subtractDays }).format('YYYY/MM/DD');
  } else {
    return now.subtract({ days }).format('YYYY/MM/DD');
  }
};

export const fetchWaterConsentData = async (property) => {
  const propertyWaterConsent = property?.property_hilltop_consent;
  if (!propertyWaterConsent) {
    throw new NoWaterConsentError('Property has no water consent.');
  }
  const hilltopSiteId = propertyWaterConsent.hilltop_site_id;
  const periodDays = propertyWaterConsent.period_days;
  const cacheName = 'hilltop-api-water-consent-cache';
  const endDate = moment().format('YYYY/MM/DD');
  const startDate = waterConsentStartDate(periodDays);
  const url = `https://data.hbrc.govt.nz/EnviroData/WaterUse.hts?Service=Hilltop&Request=GetData&Site=${hilltopSiteId}&Measurement=Total%20for%20Consent&From=${startDate}&To=${endDate}`;
  const cachedResponse = await getCachedResponse(cacheName, url);
  if (cachedResponse) {
    if (!cachedResponse.ok) {
      throw new WaterConsentDataFetchError(
        'Failed to fetch hilltop water consent.'
      );
    }
    const xmlText = await cachedResponse.text();
    const values = parseXmlToArray(xmlText);
    if (values.length === 0) {
      throw new NoWaterConsentDataError('No water consent data found.');
    }
    return values;
  } else {
    const response = await fetch(url);
    const cacheTTL = 60 * 60 * 24; // 24 hours.
    await cacheWithTTL(cacheName, url, response.clone(), cacheTTL);
    const xmlText = await response.text();
    const values = parseXmlToArray(xmlText);
    if (values.length === 0) {
      throw new NoWaterConsentDataError('No water consent data found.');
    }
    return values;
  }
};

export const waterConsentPeriodString = (days, plural) => {
  switch (days) {
    case 7:
      const value7 = '7 day';
      return plural ? value7 + 's' : value7;
    case 28:
      const value28 = '28 day';
      return plural ? value28 + 's' : value28;
    case 365:
      return '1 year';
    default:
      return '-';
  }
};

